import React, { useState, useRef, useEffect, useContext } from 'react';

import CloseIcon from "@mui/icons-material/Close";
import { InputSelector } from '../../UI/InputSelector';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { Notification } from '../../UI/Notifications';
import { handleError } from '../../../utils/custom/errorHandler';
import { createPayment } from '../../../utils/api/Lead/Payments/createPaymentApi';
import styles from "../../../styles/components/Modal/Payments/AddPaymentsModal.module.css"
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { getTransactionActionLabel } from '../../../utils/transactionActionLabels';

const translations = {
    ru: {
        title: "Добавить транзакцию",
        action: "Действие",
        assets: "Актив",
        amount: "Сумма",
        status: "Статус",
        bank: "Банк",
        wallet: "Кошелек",
        number: "Номер",
        comment: "Комментарий",
        creating: "Создание...",
        confirm: "Подтвердить",
    },
    en: {
        title: "Add Transaction",
        action: "Action",
        assets: "Assets",
        amount: "Amount",
        status: "Status",
        bank: "Bank",
        wallet: "Wallet",
        number: "Number",
        comment: "Comment",
        creating: "Creating...",
        confirm: "Confirm",
    },
};

export const AddPaymentModal = ({ leadId, onClose, onPaymentCreated }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.en;

    const modalRef = useRef(null);
    const [formData, setFormData] = useState({
        action: '',
        assets: 'usd',
        amount: '',
        status: '',
        bank: '',
        wallet: '',
        number: '',
        comment: '',
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(false);

    const handleChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (touched[key] || errors[key]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                if (newErrors[key]) delete newErrors[key];
                return newErrors;
            });
        }
    };

    const fields = [
        {
            key: 'action',
            label: t.action,
            type: 'dropdown',
            options: [
                {value: 'deposit', label: getTransactionActionLabel('deposit', langSelected)},
                {value: 'withdraw', label: getTransactionActionLabel('withdraw', langSelected)},
                {value: 'bonus', label: getTransactionActionLabel('bonus', langSelected)},
                {value: 'bank_liquidity', label: getTransactionActionLabel('bank_liquidity', langSelected)},
            ],
            required: true
        },
        {
            key: 'assets',
            label: t.assets,
            type: 'dropdown',
            options: [
                { value: 'usd', label: 'USD' },
            ],
            required: true
        },
        { key: 'amount', label: t.amount, type: 'input', required: true, min: 0, step: 'any' },
        {
            key: 'status',
            label: t.status,
            type: 'dropdown',
            options: [
                { value: 'success', label: 'Success' },
                { value: 'process', label: 'Process' },
                { value: 'fail', label: 'Fail' },
                { value: 'canceled', label: 'Canceled' },
                { value: 'waitaccept', label: 'Wait Accept' }
            ],
            required: true
        },
        { key: 'bank', label: t.bank, type: 'input', required: false },
        { key: 'wallet', label: t.wallet, type: 'input', required: false },
        { key: 'number', label: t.number, type: 'input', required: false },
        { key: 'comment', label: t.comment, type: 'input', required: false },
    ];

    const inputFieldsConfig = fields.filter((field) => field.type === "input");
    const dropdownFieldsConfig = fields.filter((field) => field.type === "dropdown");

    const handleFocus = (key) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[key]) delete newErrors[key];
            return newErrors;
        });
    };

    const handleBlur = (key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handleSubmit = async () => {
        setErrors({});
        setLoading(true);

        try {
            const dataToSend = { ...formData };
            if (dataToSend.amount === '') {
                dataToSend.amount = null;
            } else {
                dataToSend.amount = parseFloat(dataToSend.amount);
            }
            
            ['bank', 'wallet', 'number', 'comment'].forEach(key => {
                if (dataToSend[key] === '') {
                    dataToSend[key] = null;
                }
            });

            await createPayment(leadId, dataToSend);
            onPaymentCreated();
        } catch (err) {
            handleError(err.response?.data || err, setErrors);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const modalClass = `${styles.modal} ${theme === 'dark' ? styles.darkModal : ''}`;
    const filtersContainerClass = `${styles.filtersContainer} ${theme === 'dark' ? styles.darkFiltersContainer : ''}`;
    const modalLabelClass = `${styles.modalLabel} ${theme === 'dark' ? styles.darkModalLabel : ''}`;
    const modalLabelPClass = `${styles.modalLabel} ${styles.p} ${theme === 'dark' ? styles.darkModalLabelP : ''}`;
    const modalContentClass = `${styles.modalContent} ${theme === 'dark' ? styles.darkModalContent : ''}`;
    const inputRowLabelClass = `${styles.inputRow} ${styles.label} ${theme === 'dark' ? styles.darkInputRowLabel : ''}`;
    const modalFooterClass = `${styles.modalFooter} ${theme === 'dark' ? styles.darkModalFooter : ''}`;
    const submitButtonClass = `${styles.submitButton} ${theme === 'dark' ? styles.darkSubmitButton : ''}`;

    return (

        <>  
        {errors.general && <Notification message={errors.general} type={"error"} />}
        <div className={modalClass}>

            <div className={filtersContainerClass} ref={modalRef}>
                <div className={modalLabelClass}>
                    <p className={modalLabelPClass}>{t.title}</p>
                    <div onClick={onClose} className={theme === 'dark' ? styles.darkCloseIcon : ''}>
                        <CloseIcon />
                    </div>
                </div>

                <div className={modalContentClass}>
                    {dropdownFieldsConfig.map((field) => (
                        <div
                            key={field.key}
                            className={styles.inputRow}
                            style={{ gridColumn: 'span 6' }}
                        >
                            <label className={inputRowLabelClass}>{field.label}:</label>
                            <DropdownSelector
                                filterName={field.key}
                                selectedValue={formData[field.key]}
                                handleFilterChange={handleChange}
                                disabled={field.key === 'assets'}
                                options={field.options || []}
                                onFocus={() => handleFocus(field.key)}
                                onBlur={() => handleBlur(field.key)}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                theme={theme} 
                            />
                        </div>
                    ))}

                    {inputFieldsConfig.map((field) => (
                        <div
                            key={field.key}
                            className={styles.inputRow}
                            style={{ gridColumn: 'span 6' }}
                        >
                            <label className={inputRowLabelClass}>{field.label}:</label>
                            <InputSelector
                                filterName={field.key}
                                selectedValue={formData[field.key]}
                                handleFilterChange={handleChange}
                                onFocus={() => handleFocus(field.key)}
                                onBlur={() => handleBlur(field.key)}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                type={field.key === 'amount' ? 'number' : 'text'}
                                isTouched={touched[field.key]}
                                step={field.step}
                                min={field.min}
                                theme={theme}
                            />
                        </div>
                    ))}
                </div>

                <div className={modalFooterClass}>
                    <div className={styles.confirmationWrapper}>
                        <button className={submitButtonClass} onClick={handleSubmit} disabled={loading}>
                            {loading ? t.creating : t.confirm}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};
